import { ReactNode, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./style.module.scss";
import PrintPage, { EXPORT_PDF_QUERY_PARAM } from "@components/print/PrintElements/PrintPage";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import PrintPageFooter from "@components/print/PrintElements/PrintPageFooter";
import { useAppSelector } from "@/store/hooks";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import DocumentInfo from "@components/print/PrintElements/DocumentInfo";
import ProductList from "@components/print/PrintElements/ProductList";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { CreditnoteFormDataKeys } from "@/generated/CreditnoteFormDataKeys";
import { CreditnoteRelatedAttributes } from "@/formTypes/CreditnoteDataType";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { readDocumentOtherChargesTotal } from "@utils/DocumentOtherCharges";
import { readDocumentOtherChargesLabel } from "@utils/ClientShipping";
import { formatDate } from "@utils/Api";
import { CustomerInfoBase } from "@/formTypes/DocumentClientType";
import { chunkArray, stripHtmlTags } from "@utils/Utils";
import { sortProductsByOrder } from "@utils/EDocument";
import { checkCreditnoteTicketPrint } from "@utils/ticketPrintUtils";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { BuyInvoiceFormDataKeys } from "@/generated/BuyInvoiceFormDataKeys";
import { buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";

const CreditnotePrint = () => {
  const params = useParams()
  const { id: creditnoteId, autoPrint = false, copies = 0 } = params;

  const suscription = useAppSelector((state) => { return state.suscription.suscription });
  const maxItemPerPage = Number(suscription?.items_per_page || 30)
  const [buyInvoice, setBuyInvoice] = useState<BuyInvoiceDataType>({})
  const [creditnote, setCreditnote] = useState<CreditnoteDataType>({})
  const [creditnoteProdCount, setCreditnoteProdCount] = useState<number>(0)
  const [creditnotesProducts, setCreditnotesProducts] = useState<EDocumentProductDataType[]>()
  const [documentPages, setDocumentPages] = useState<EDocumentProductDataType[][]>()
  const [searchParams] = useSearchParams();
  const exportFlag = searchParams.get(EXPORT_PDF_QUERY_PARAM);

  useEffect(() => {
    import("@printerVariation/generic.scss");
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadCreditnoteData()
  }, []);

  useEffect(() => {
    if (!exportFlag && autoPrint && suscription?.id && creditnote?.id) {
      checkCreditnoteTicketPrint(suscription, creditnote)
    }
  }, [suscription?.id, creditnote?.id]);

  useEffect(() => {
    const pages = chunkArray(creditnotesProducts || [], maxItemPerPage)
    setDocumentPages(pages)
  }, [maxItemPerPage, creditnotesProducts]);

  const loadCreditnoteData = async () => {
    if (creditnoteId) {
      const creditnote = await CreditnoteApi.get(creditnoteId, {
        selectionSet: [
          ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "invoice.*",
          "creditnoteProducts.*",
          "creditnoteProducts.product.*"
        ]
      })
      if (creditnote.buyInvoiceId) {
        let buyInvoice = await BuyInvoiceApi.get(creditnote.buyInvoiceId, {
          selectionSet: [
            ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "buyInvoiceProducts.*",
          ]
        })
        setBuyInvoice(buyInvoice)
      }
      setCreditnotesProducts(sortProductsByOrder(creditnote.creditnoteProducts))
      setCreditnotesProducts(creditnote.creditnoteProducts)
      setCreditnoteProdCount(creditnote.creditnoteProducts?.length || 0)
      setCreditnote(creditnote)
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  function toCustomerInfoBase(creditNote: CreditnoteDataType, buyInvoice?: BuyInvoiceDataType): CustomerInfoBase {
    const { invoice } = creditNote
    let invoiceData = buyInvoice?.id ? buyInvoice : invoice

    if (!invoiceData.nombre_factura) throw new Error("nombre_factura requerido");
    return {
      nombre_factura: invoiceData.nombre_factura,
      razon_social: invoiceData.razon_social ?? "",
      tel1: invoiceData.tel1 ?? "",
      tel2: invoiceData.tel2 ?? "",
      email: invoiceData.email ?? "",
      direccion: invoiceData.direccion ?? "",
      clientActivitycode: invoiceData.clientActivitycode ?? "",
      plazo: invoiceData.plazo ?? null,
      client: invoiceData.client ?? null,
      tipo_identificacion: invoiceData.tipo_identificacion ?? "",
      identificacion: invoiceData.identificacion ?? "",
    };
  }

  const renderPage = (documentPages: EDocumentProductDataType[][]): ReactNode[] => {
    return documentPages.map((page, index) => {
      return (
        <PrintPage
          autoPrint={autoPrint === "1"}
          copies={Number(copies)}
        >
          <PrintPageHeader suscription={suscription} />
          <div className="page-content">
            <PrintDocumentHeader title={`Nota de Credito #${creditnote.numero}`} fecha={formatDate(creditnote.created, "YYYY-MM-DD", "DD.MM.YY")} />
            <DocumentInfo
              customer={toCustomerInfoBase(creditnote, buyInvoice)}
              docType="Factura"
              referenceData={{
                title: `Sobre Factura ${buyInvoice?.numero ? 'de Compra' : ''}`,
                value: ` #${creditnote.invoice?.numero ? creditnote.invoice.numero : buyInvoice.numero}`
              }}
              docSequenceData={{
                docKey: creditnote.document_key || "",
                sequence: creditnote.sequence || ""
              }}
            />
            <ProductList
              maxItemPerPage={maxItemPerPage}
              products={page}
              productCount={creditnoteProdCount}
            />
            <PrintPageFooter
              currencyCode={creditnote.currency.currency_id}
              exchangeRate={creditnote.exchange_rate || 0}
              summary={{
                subtotal: creditnote.subtotal || 0,
                descuento: creditnote.descuento || 0,
                impuestos: creditnote.impuestos || 0,
                total: creditnote.total || 0,
                otrosCargos: readDocumentOtherChargesTotal(creditnote),
                otrosCargosLabel: readDocumentOtherChargesLabel(creditnote)
              }}
              comments={[
                `Por concepto de: ${stripHtmlTags(`${creditnote.description}`)}`,
                `${suscription?.invoice_info}`,
              ]}
              currentPage={(index + 1)}
              totalPages={documentPages.length} />
          </div>

        </PrintPage>
      )
    });
  }

  return (
    <div>
      {creditnote?.id && documentPages && renderPage(documentPages)}
      {!creditnote.id && (
        <h3>Cargando </h3>
      )}

    </div>
  );
}

export default CreditnotePrint;
